import {RestService} from "@/services/rest/RestService";
import {
    CatalogProductAPIApi,
    ProductPriceAPIApi,
    ProductPropertyAPIApi,
    ProductStockAPIApi
} from "@/api/pharma-cpc-product-mgmt";
import {
    CatalogProductDetailRestDto,
    CatalogProductOverviewRestDto,
    CustomerProductPriceForProductRestDto,
    CustomerProductPriceRestDto,
    CustomerProductPropertiesResultsRestDto,
    CustomerProductPropertyRestDto,
    MaximumOrderQuantityRestDto,
    MinimalStockRestDto,
    PagedCatalogProductOverviewResultsRestDto,
    ProductBackInStockNotificationRequestResultsRestDto,
    ProductCustomNameRestDto,
    ProductCustomNamesOverviewRestDto,
    ProductPriceInfoRestDto,
    ProductStockInfoRestDto
} from "@/api/pharma-cpc-product-mgmt/models";
import {PimProductsPagingModel} from "@/models/paging/PimProductsPagingModel";
import {PhotoRestService} from "@/services/rest/photo/PhotoRestService";
import {CustomerContext} from "@/context/CustomerContext";
import {CacheKeyEnum} from "@/services/cache/CacheKeyEnum";
import {CacheService} from "@/services/cache/CacheService";
import {trimLeadingZeroes} from "@/helpers/functions/number";
import {CatalogProductsCriteriaBodyRestDtoModel} from "@/models/criteria/CatalogProductsCriteriaBodyRestDtoModel";
import {
    ProductsBulkActionRequestRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/ProductsBulkActionRequestRestDtoModel";
import {I18nContext} from "@/context/I18nContext";
import {AxiosPromise, AxiosResponse} from "axios";
import {
    PagedCatalogProductWithImageOverviewResultsRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/PagedCatalogProductWithImageOverviewResultsRestDtoModel";
import {
    CatalogProductWithImageOverviewRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CatalogProductWithImageOverviewRestDtoModel";
import {
    CatalogProductWithImageDetailRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CatalogProductWithImageDetailRestDtoModel";

export class CatalogProductsRestService extends RestService {

    private apiService: CatalogProductAPIApi;
    private propertiesApiService: ProductPropertyAPIApi;
    private priceApiService: ProductPriceAPIApi;
    private stockApiService: ProductStockAPIApi;

    protected constructor() {
        super();

        this.apiService = new CatalogProductAPIApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cpcUrl);
        this.propertiesApiService = new ProductPropertyAPIApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cpcUrl);
        this.priceApiService = new ProductPriceAPIApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cpcUrl);
        this.stockApiService = new ProductStockAPIApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cpcUrl);
    }

    public async findProductCustomNames(productCode: string): Promise<ProductCustomNamesOverviewRestDto> {
        const response = await this.propertiesApiService.findProductCustomNames(productCode);

        return response.data;
    }

    public async syncProductCustomNames(productCode: string, dto: ProductCustomNameRestDto): Promise<void> {
        await this.propertiesApiService.syncProductCustomNames(dto, productCode);
    }

    public async syncProductCustomerPrice(productCode: string, body: CustomerProductPriceRestDto): Promise<void> {
        await this.priceApiService.syncProductCustomerPrice(body, productCode);
    }

    public async deleteProductCustomerPrice(productCode: string): Promise<void> {
        await this.priceApiService.deleteProductCustomerPrice(productCode);
    }

    public async findProductCustomerPrice(productCode: string): Promise<CustomerProductPriceForProductRestDto> {
        const response = await this.priceApiService.findProductCustomerPrice(productCode, null);

        return response.data;
    }

    public async findProductPriceInfo(productCode: string): Promise<ProductPriceInfoRestDto> {
        const response = await this.priceApiService.findProductPriceInfo(productCode, null);

        return response.data;
    }

    public async findProductStockInfo(productCode: string): Promise<ProductStockInfoRestDto> {
        const response = await this.stockApiService.findProductStockInfo(productCode);

        return response.data;
    }

    public async findProductBackInStockNotificationRequests(productCode: string): Promise<ProductBackInStockNotificationRequestResultsRestDto> {
        const response = await this.stockApiService.findProductBackInStockNotificationRequests(productCode);

        return response.data;
    }

    public async syncProductCustomerMaximumOrderQuantity(productCode: string, body: MaximumOrderQuantityRestDto): Promise<void> {
        await this.stockApiService.syncProductMaximumOrderQuantity(body, productCode);
    }

    public async deleteProductCustomerMaximumOrderQuantity(productCode: string): Promise<void> {
        await this.stockApiService.deleteProductMaximumOrderQuantity(productCode);
    }

    public async findProductCustomerMaximumOrderQuantity(productCode: string): Promise<MaximumOrderQuantityRestDto> {
        const response: AxiosResponse<MaximumOrderQuantityRestDto> = await this.stockApiService.findProductMaximumOrderQuantity(productCode);

        return response.data;
    }

    public async syncProductCustomerMinimalStock(productCode: string, body: MinimalStockRestDto): Promise<void> {
        await this.stockApiService.syncProductMinimalStock(body, productCode);
    }

    public async deleteProductCustomerMinimalStock(productCode: string): Promise<void> {
        await this.stockApiService.deleteProductMinimalStock(productCode);
    }

    public async findProductCustomerMinimalStock(productCode: string): Promise<MinimalStockRestDto> {
        const response: AxiosResponse<MinimalStockRestDto> = await this.stockApiService.findProductMinimalStock(productCode);

        return response.data;
    }

    public async findCustomerProductProperties(productCode: string, languageCode: string, typecode: string |null): Promise<CustomerProductPropertiesResultsRestDto> {
        const response = await this.propertiesApiService.findCustomerProductProperties(productCode, null, languageCode, typecode);

        return response.data;
    }

    public async deleteProductProperty(productCode: string, typeCode: string, lanuageCode: string): Promise<void> {
        await this.propertiesApiService.deleteProductProperty(productCode, typeCode, lanuageCode);
    }

    public async syncProductProperty(productCode: string, dto: CustomerProductPropertyRestDto): Promise<void> {
        await this.propertiesApiService.syncProductProperty(dto, productCode);
    }

    public async bulkActionClearCustomSort(
        criteria: CatalogProductsCriteriaBodyRestDtoModel,
    ): Promise<void> {
        const body = ProductsBulkActionRequestRestDtoModel.createWithDefaults(criteria);
        body.clear_custom_sort_weight = true;
        await this.apiService.requestBulkActionForProducts(body, null, null, I18nContext.getDefaultLanguage());
    }

    public async bulkActionClearCustomStandardPrices(
        criteria: CatalogProductsCriteriaBodyRestDtoModel,
    ): Promise<void> {
        const body = ProductsBulkActionRequestRestDtoModel.createWithDefaults(criteria);
        body.clear_custom_standard_price = true;
        await this.apiService.requestBulkActionForProducts(body, null, null, I18nContext.getDefaultLanguage());
    }

    public async bulkActionClearCustomNamesNl(
        criteria: CatalogProductsCriteriaBodyRestDtoModel,
    ): Promise<void> {
        const body = ProductsBulkActionRequestRestDtoModel.createWithDefaults(criteria);
        body.clear_custom_name_nl = true;
        await this.apiService.requestBulkActionForProducts(body, null, null, I18nContext.getDefaultLanguage());
    }

    public async bulkActionClearCustomNamesFr(
        criteria: CatalogProductsCriteriaBodyRestDtoModel,
    ): Promise<void> {
        const body = ProductsBulkActionRequestRestDtoModel.createWithDefaults(criteria);
        body.clear_custom_name_fr = true;
        await this.apiService.requestBulkActionForProducts(body, null, null, I18nContext.getDefaultLanguage());
    }

    public async findCatalogProducts(
        criteria: CatalogProductsCriteriaBodyRestDtoModel,
        pagingModel: PimProductsPagingModel,
        languageCode: string
    ): Promise<PagedCatalogProductWithImageOverviewResultsRestDtoModel> {
        const cacheKey: string =
            `${CacheKeyEnum.PRODUCTS}_${JSON.stringify(criteria)}_${JSON.stringify(pagingModel)}_${languageCode}`;

        let promise: Promise<AxiosResponse<PagedCatalogProductOverviewResultsRestDto>> =
            CacheService.getInstance().get(cacheKey);

        if (promise !== null) {
            const resultFromApiCall: AxiosResponse<PagedCatalogProductOverviewResultsRestDto> = await promise;

            const result: PagedCatalogProductWithImageOverviewResultsRestDtoModel = new PagedCatalogProductWithImageOverviewResultsRestDtoModel(
                resultFromApiCall.data.paging_metadata ?? null,
                resultFromApiCall.data.results.map((product: CatalogProductOverviewRestDto) => new CatalogProductWithImageOverviewRestDtoModel(product))
            );

            await PhotoRestService.getInstance().fillProductImageUrls(result.results, languageCode);

            return result;
        }

        const pageSize = pagingModel?.size ? Math.min(parseInt(pagingModel.size), 500) : undefined;
        promise = this.apiService.findCatalogProductsNew(
            criteria,
            null,
            null,
            languageCode,
            pagingModel.number,
            pageSize)
        ;
        CacheService.getInstance().set(cacheKey, promise, null, 58 * 60_000);

        const resultFromApiCall: AxiosResponse<PagedCatalogProductOverviewResultsRestDto> = await promise;
        const result: PagedCatalogProductWithImageOverviewResultsRestDtoModel = new PagedCatalogProductWithImageOverviewResultsRestDtoModel(
            resultFromApiCall.data.paging_metadata ?? null,
            resultFromApiCall.data.results.map((product: CatalogProductOverviewRestDto) => new CatalogProductWithImageOverviewRestDtoModel(product))
        )

        await PhotoRestService.getInstance().fillProductImageUrls(result.results, languageCode);

        return result;
    }


    public async findCatalogProductsManyAll(
        cnkCodes: string[],
        productCodes: string[],
        language: string
    ): Promise<CatalogProductWithImageOverviewRestDtoModel[]> {
        if (!cnkCodes.length && !productCodes.length) {
            return [];
        }

        const customerCode = CustomerContext.getCustomerCode();
        const cacheKeyPrefix: string = CacheKeyEnum.CATALOG_PRODUCT_OVERVIEW
            + '.' + (language ?? '--')
            + '.';

        const result: CatalogProductWithImageOverviewRestDtoModel[] = [];

        let missingCnkCodes: string[] = [];
        let missingProductCodes: string[] = [];

        cnkCodes.forEach((cnkCode) => {
            const trimmedCnKCode = trimLeadingZeroes(cnkCode);
            const dto: CatalogProductWithImageOverviewRestDtoModel | null = CacheService.getInstance().get(
                cacheKeyPrefix + '_CNK_' + trimmedCnKCode,
                customerCode
            );
            if (dto) {
                result.push(dto);
            } else {
                missingCnkCodes.push(trimmedCnKCode);
            }
        })

        productCodes.forEach((productCode) => {
            const dto: CatalogProductWithImageOverviewRestDtoModel | null = CacheService.getInstance().get(
                cacheKeyPrefix + '_PRODUCT_CODE_' + productCode,
                customerCode
            );
            if (dto) {
                result.push(dto);
            } else {
                missingProductCodes.push(productCode);
            }
        })

        while (missingCnkCodes.length || missingProductCodes.length) {
            const cnkCodesToFetch: string[] = missingCnkCodes.slice(0, Math.min(missingCnkCodes.length, 500));
            missingCnkCodes = missingCnkCodes.slice(Math.min(missingCnkCodes.length, 500), missingCnkCodes.length);

            const maxNumberOfProductCodesToFetch = 500 - cnkCodesToFetch.length;
            const productCodesToFetch: string[] = missingProductCodes.slice(
                0,
                Math.min(missingProductCodes.length, maxNumberOfProductCodesToFetch)
            );
            missingProductCodes = missingProductCodes.slice(
                Math.min(missingProductCodes.length, maxNumberOfProductCodesToFetch),
                missingProductCodes.length
            );

            const criteria = CatalogProductsCriteriaBodyRestDtoModel.createWithDefaults();
            criteria.filter_cnk_codes = cnkCodesToFetch;
            criteria.filter_product_codes = productCodesToFetch;
            const pagingModel = PimProductsPagingModel.createWithDefaults();
            pagingModel.size = '' + cnkCodesToFetch.length + productCodesToFetch.length;
            const nextBatch: PagedCatalogProductWithImageOverviewResultsRestDtoModel = await this.findCatalogProducts(criteria, pagingModel, language);
            const dtos: CatalogProductWithImageOverviewRestDtoModel[] = nextBatch.results;

            dtos.forEach((dto: CatalogProductWithImageOverviewRestDtoModel) => {
                const trimmedCnKCode: string = trimLeadingZeroes(dto.product.product_info.cnk_code ?? '');

                // Cache DTOs based on cnk code AND based on product code.
                if (trimmedCnKCode !== '') {
                    CacheService.getInstance().set(
                        cacheKeyPrefix + '_CNK_' + trimmedCnKCode,
                        dto,
                        customerCode,
                        58 * 60_000 /* URLs are valid for 60 minutes, so cache the DTOs for 58 minutes */
                    );
                }
                if (dto.product.product_code !== '') {
                    CacheService.getInstance().set(
                        cacheKeyPrefix + '_PRODUCT_CODE_' + dto.product.product_code,
                        dto,
                        customerCode,
                        58 * 60_000 /* URLs are valid for 60 minutes, so cache the DTOs for 58 minutes */
                    );
                }

                result.push(dto);
            })
        }

        return result;
    }

    public async getCatalogProductDetail(productCode: string, language: string): Promise<CatalogProductWithImageDetailRestDtoModel> {
        const response: AxiosResponse<CatalogProductDetailRestDto> = await this.apiService.getCatalogProductDetail(productCode);

        const imageUrl: string = await PhotoRestService.getInstance()
            .getProductImageUrl(response.data.product_info.cnk_code, response.data.product_code, language);

        return new CatalogProductWithImageDetailRestDtoModel(response.data, imageUrl);
    }

}
